import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Trash2, Pin, Lock, MessageSquare } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// Minimal markdown-lite render: **bold**, *italic*, and preserve line breaks
function renderContent(text) {
  const parts = [];
  const escapeHtml = (s) => s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]);
  let html = escapeHtml(text);
  html = html.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
  html = html.replace(/\*(.+?)\*/g, "<em>$1</em>");
  html = html.replace(/\n/g, "<br/>");
  return html;
}

export default function ThreadDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [thread, setThread] = useState(null);
  const [comments, setComments] = useState([]);
  const [content, setContent] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      const t = await api.get(`/forum/threads/${id}`);
      setThread(t.data);
      const c = await api.get(`/forum/threads/${id}/comments`);
      setComments(c.data);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const isAdmin = user?.role === "admin";
  const canReply = user && (user.role === "member" || user.role === "admin") && thread && !thread.locked;

  const submitReply = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      await api.post(`/forum/threads/${id}/comments`, { content });
      setContent("");
      await load();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (patch) => {
    await api.patch(`/forum/threads/${id}`, null, { params: patch });
    await load();
  };

  const del = async () => {
    if (!window.confirm("Delete this thread?")) return;
    await api.delete(`/forum/threads/${id}`);
    nav("/forum");
  };

  const delComment = async (cid) => {
    if (!window.confirm("Delete this comment?")) return;
    await api.delete(`/forum/comments/${cid}`);
    await load();
  };

  if (!thread) return <div className="min-h-screen bg-black text-zinc-500 flex items-center justify-center font-mono text-xs">Loading...</div>;

  return (
    <div data-testid="thread-detail-page" className="min-h-screen bg-black text-white">
      <div className="border-b border-zinc-900 bg-black/80 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/forum" data-testid="thread-back" className="flex items-center gap-2 text-white hover:text-sky-400">
            <ArrowLeft className="w-4 h-4" /> <span className="font-mono text-xs uppercase tracking-widest">Back to Forum</span>
          </Link>
          <span className="text-xs font-mono text-zinc-500">{user?.name} · <span className="text-sky-400 uppercase">{user?.role}</span></span>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-10">
        <div className="flex items-center gap-2 flex-wrap mb-4">
          {thread.pinned && <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-sky-400"><Pin className="w-3 h-3" /> Pinned</span>}
          {thread.locked && <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-red-400"><Lock className="w-3 h-3" /> Locked</span>}
        </div>
        <h1 data-testid="thread-title" className="font-display text-3xl md:text-4xl uppercase leading-tight">{thread.title}</h1>
        <div className="text-xs font-mono text-zinc-500 mt-2">by {thread.author_name} · {new Date(thread.created_at).toLocaleString()}</div>

        {isAdmin && (
          <div className="mt-4 flex flex-wrap gap-2">
            <button onClick={() => toggle({ pinned: !thread.pinned })} data-testid="admin-pin-toggle" className="text-[10px] font-mono uppercase tracking-widest px-3 py-1.5 border border-zinc-800 hover:border-sky-400 hover:text-sky-400">{thread.pinned ? "Unpin" : "Pin"}</button>
            <button onClick={() => toggle({ locked: !thread.locked })} data-testid="admin-lock-toggle" className="text-[10px] font-mono uppercase tracking-widest px-3 py-1.5 border border-zinc-800 hover:border-sky-400 hover:text-sky-400">{thread.locked ? "Unlock" : "Lock"}</button>
            <button onClick={del} data-testid="admin-delete-thread" className="text-[10px] font-mono uppercase tracking-widest px-3 py-1.5 border border-red-900 text-red-400 hover:border-red-500">Delete</button>
          </div>
        )}

        <article className="mt-8 border border-zinc-800 bg-zinc-950/60 p-6 md:p-8">
          <div className="prose prose-invert max-w-none text-zinc-200 text-sm md:text-base leading-relaxed" dangerouslySetInnerHTML={{ __html: renderContent(thread.content) }} />
        </article>

        <div className="mt-12">
          <div className="flex items-center gap-2 mb-4">
            <MessageSquare className="w-4 h-4 text-sky-400" />
            <span className="font-mono text-[11px] tracking-[0.3em] text-sky-400 uppercase">{comments.length} Replies</span>
          </div>

          <ul className="space-y-3">
            {comments.map((c) => (
              <li key={c.id} data-testid={`comment-${c.id}`} className="border border-zinc-900 bg-zinc-950/40 p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs font-mono text-zinc-400"><span className="text-sky-400">{c.author_name}</span> · {new Date(c.created_at).toLocaleString()}</div>
                  {isAdmin && <button onClick={() => delComment(c.id)} data-testid={`admin-del-comment-${c.id}`} className="text-red-400 hover:text-red-300"><Trash2 className="w-3.5 h-3.5" /></button>}
                </div>
                <div className="text-sm text-zinc-200" dangerouslySetInnerHTML={{ __html: renderContent(c.content) }} />
              </li>
            ))}
          </ul>

          {canReply ? (
            <form onSubmit={submitReply} className="mt-6 border border-zinc-800 bg-zinc-950/40 p-4">
              <textarea rows={4} value={content} onChange={(e) => setContent(e.target.value)} required data-testid="reply-textarea" placeholder="Γραψε την απαντηση σου..." className="w-full bg-black border border-zinc-800 text-white px-4 py-3 focus:outline-none focus:border-sky-400 font-mono text-sm" />
              {err && <div className="text-red-400 text-xs font-mono mt-2">{err}</div>}
              <div className="flex justify-end mt-3">
                <button type="submit" disabled={busy} data-testid="reply-submit" className="bg-sky-400 text-black px-5 py-2 text-xs font-bold uppercase tracking-widest hover:bg-sky-300 disabled:opacity-40">{busy ? "..." : "Reply"}</button>
              </div>
            </form>
          ) : (
            <div className="mt-6 text-center text-zinc-500 text-sm">
              {thread.locked ? "Το thread ειναι κλειδωμενο." : user ? user.role === "pending" ? "Ο λογαριασμος σου περιμενει εγκριση." : "Δεν εχεις δικαιωμα να απαντησεις." : (<><Link to="/login" className="text-sky-400 hover:underline">Sign in</Link> για να απαντησεις.</>)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
